import { getNestedOrFloatingElementById } from '@fs/zion-sls'

/**
 * Compares two linkedPersonIds arrays and identifies additions and removals
 * @param {Array<{id: string, partitionKey: string}>} originalLinkedPersons - Original linked persons from form default values
 * @param {Array<{id: string, partitionKey: string}>} currentLinkedPersons - Current linked persons from form values
 * @returns {{addedPersons: Array<{id: string, partitionKey: string}>, removedPersons: Array<{id: string, partitionKey: string}>}}
 */
export const compareLinkedPersons = (originalLinkedPersons = [], currentLinkedPersons = []) => {
  const originalIds = new Set(originalLinkedPersons.map((p) => (typeof p === 'string' ? p : p.id)))
  const currentIds = new Set(currentLinkedPersons.map((p) => (typeof p === 'string' ? p : p.id)))

  const addedPersons = currentLinkedPersons.filter((person) => {
    const personId = typeof person === 'string' ? person : person.id
    return !originalIds.has(personId)
  })

  const removedPersons = originalLinkedPersons.filter((person) => {
    const personId = typeof person === 'string' ? person : person.id
    return !currentIds.has(personId)
  })

  return { addedPersons, removedPersons }
}

/**
 * Determines the appropriate shared element reference based on SLS hierarchy
 *
 * In SLS, fields can be shared at two levels:
 * - FIELD level (leaf node): When multiple intermediate elements point to the same field
 *   Example: Person1→NAME_SURNAME→NAME_SURN("Bush") ←NAME_SURNAME←Person2
 * - INTERMEDIATE level: When multiple NAME elements point to the same intermediate
 *   Example: Person1→NAME→NAME_SURNAME ←NAME←Person2
 *
 * This function determines which level is shared by checking the field's superElements count:
 * - If superElements.length > 1: The field itself is shared (return the field)
 * - If superElements.length === 1: Not shared, return the intermediate element for potential future sharing
 *
 * @param {Array} images - SLS images array containing all elements
 * @param {string} fieldId - ID of the field to check for sharing
 * @returns {Object|undefined} The shared element (field if shared, intermediate if not)
 */
export const getSharedElement = (images, fieldId) => {
  const slsField = getNestedOrFloatingElementById(images, fieldId)
  const fieldSuperElements = slsField?.superElements || []

  if (slsField?.superElements?.length > 1) {
    return slsField
  }

  // If not shared, return the intermediate element
  const firstSuperElementId = fieldSuperElements[0]?.id
  const intermediateElement = getNestedOrFloatingElementById(images, firstSuperElementId)

  return intermediateElement
}
